
// packages/api-client/src/services/visualizationService.ts
import { getApiClient } from '../client';
import type { VisualizationLayout, LayoutSummary, ServiceResponse } from '@dopomogai/types';
import type { UUID } from '@dopomogai/types';
import { handleApiError } from '../utils/handleApiError';

// Helper: normalize _id to id for all payloads
function normalizeLayout<T extends any>(data: T): T {
  if (data && typeof data === 'object' && (data as any)._id && !(data as any).id) {
    (data as any).id = (data as any)._id;
    delete (data as any)._id;
  }
  return data;
}

function normalizeLayoutsArray<T extends any[]>(items: T): T {
  return items.map((it: any) => normalizeLayout(it)) as T;
}

// Define a clearer type for the creation payload
type VisualizationLayoutCreate = Pick<VisualizationLayout, 'name' | 'description'>;

const getLayout = (layoutId: UUID): ServiceResponse<VisualizationLayout> => {
  return getApiClient()
    .get(`/visualization/layouts/${layoutId}`)
    .then(res => normalizeLayout(res.data))
    .catch(err => handleApiError(err, `fetching layout '${layoutId}'`));
};

const listLayouts = (): ServiceResponse<LayoutSummary[]> => {
  return getApiClient()
    .get(`/visualization/layouts`)
    .then(res => normalizeLayoutsArray(res.data))
    .catch(err => handleApiError(err, 'listing layouts'));
};

const createLayout = (payload: VisualizationLayoutCreate): ServiceResponse<VisualizationLayout> => {
  return getApiClient()
    .post(`/visualization/layouts`, payload)
    .then(res => normalizeLayout(res.data))
    .catch(err => handleApiError(err, 'creating layout'));
};

const updateLayout = (layoutId: UUID, patchOps: any[]): ServiceResponse<VisualizationLayout> => {
  return getApiClient()
    .patch(`/visualization/layouts/${layoutId}`, patchOps)
    .then(res => normalizeLayout(res.data))
    .catch(err => handleApiError(err, `updating layout '${layoutId}'`));
};

export const visualizationService = {
  getLayout,
  listLayouts,
  createLayout,
  updateLayout,
};